import { useState, type InputEvent, type SubmitEvent } from 'react'
import { signInWithEmailAndPassword } from 'firebase/auth'
import { Link, useNavigate } from 'react-router'
import { authErrorMessage } from '../lib/authErrors'
import { signInWithGoogle } from '../lib/googleAuth'
import { getLastUsedMethod, rememberLastUsedMethod } from '../lib/lastUsedMethod'
import AuthCard from '../components/auth/AuthCard'
import AuthField from '../components/auth/AuthField'
import GoogleSignIn from '../components/auth/GoogleSignIn'
import { auth } from '../lib/firebase'
import { collectErrors, type FieldErrors } from '../lib/formErrors'

const MESSAGES = {
  email: 'Enter a valid email address.',
  password: 'Enter your password.',
}

export default function Login() {
  const navigate = useNavigate()
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function continueWithGoogle() {
    const signup = await signInWithGoogle()
    if (!signup) return
    navigate('/signup', {
      state: { googleCredential: signup.credential.toJSON(), googleName: signup.name },
    })
  }

  function clearFieldError(event: InputEvent<HTMLFormElement>) {
    const { name } = event.target as HTMLInputElement
    setFieldErrors((current) => ({ ...current, [name]: '' }))
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const invalid = collectErrors(form, MESSAGES)
    setFieldErrors(invalid)
    if (Object.keys(invalid).length > 0) return

    const fields = new FormData(form)
    setError('')
    setSubmitting(true)

    try {
      await signInWithEmailAndPassword(auth, String(fields.get('email')), String(fields.get('password')))
      rememberLastUsedMethod('email')
    } catch (err) {
      setError(authErrorMessage(err))
      setSubmitting(false)
    }
  }

  return (
    <AuthCard
      title="Log back in to Adlovin"
      subtitle="Pick up exactly where you left off."
      footer={
        <>
          Don't have an account yet?{' '}
          <Link to="/signup" className="font-medium text-purple-700 hover:text-purple-800">
            Sign up
          </Link>
        </>
      }
    >
      <GoogleSignIn signIn={continueWithGoogle} onError={setError} />

      <form noValidate onSubmit={handleSubmit} onInput={clearFieldError} className="space-y-4">
        <AuthField
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="Your email"
          lastUsed={getLastUsedMethod() === 'email'}
          error={fieldErrors.email}
          required
        />
        <AuthField
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          placeholder="Your password"
          error={fieldErrors.password}
          required
        />

        <div className="text-right">
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-purple-700 hover:text-purple-800"
          >
            Forgot password?
          </Link>
        </div>

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="h-11 w-full rounded-[10px] bg-linear-to-b from-stone-700 to-stone-950 text-[15px] font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_2px_rgba(28,25,23,0.2)] transition-opacity enabled:hover:opacity-90 disabled:opacity-60"
        >
          {submitting ? 'Logging in…' : 'Log in'}
        </button>
      </form>
    </AuthCard>
  )
}
